/**
 * DialogCobrarConsulta — Modal que aparece después de guardar una consulta.
 *
 * Pre-llena con un servicio del catálogo según tipo_consulta:
 *   primera_vez     → CONS-GEN / Consulta general
 *   subsecuente     → CONS-SUB / Consulta subsecuente
 *   urgencia        → CONS-URG / Consulta de urgencia
 *   interconsulta   → INT-CONS / Interconsulta
 *   valoracion      → VAL-INT  / Valoración integral
 *
 * Permite agregar/editar items, calcular total y crear el cargo.
 * Al confirmar, ofrece encadenar con "Registrar pago ahora".
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, Slide, TextField,
  Autocomplete, FormControl, InputLabel, Select, MenuItem, Alert,
  Tooltip, CircularProgress, Chip,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Skip as SkipIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12, fontWeight: 600, color: '#475569' },
};

// Mapeo tipo_consulta → palabras clave para buscar en catálogo
const TIPO_KEYWORDS = {
  primera_vez:    ['cons-gen', 'general', 'primera'],
  subsecuente:    ['cons-sub', 'subsecuente', 'control'],
  urgencia:       ['cons-urg', 'urgencia'],
  interconsulta:  ['int-cons', 'interconsulta'],
  valoracion:     ['val-int', 'valoración', 'valoracion'],
};

function fmtMoney(num, moneda = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(Number(num || 0));
}

function buscarServicioPorTipo(servicios, tipo) {
  if (!Array.isArray(servicios) || servicios.length === 0) return null;
  const keywords = TIPO_KEYWORDS[tipo] || [];
  for (const kw of keywords) {
    const match = servicios.find(s =>
      (s.codigo || '').toLowerCase().includes(kw) ||
      (s.nombre || '').toLowerCase().includes(kw)
    );
    if (match) return match;
  }
  // Fallback: primer servicio de categoría=consulta
  return servicios.find(s => s.categoria === 'consulta') || servicios[0];
}

export default function DialogCobrarConsulta({
  open, onClose, consulta, paciente, userId,
  onCargoCreado,        // (cargos) => void — se llama después de crear los cargos
  onAbrirPago,          // (cargo) => void — para encadenar con DialogNuevoPago
}) {
  const [servicios, setServicios] = useState([]);
  const [items, setItems] = useState([]);
  const [moneda, setMoneda] = useState('MXN');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [creado, setCreado] = useState(null); // cargos creados, para mostrar paso 2

  // Cargar catálogo + pre-llenar primer item
  useEffect(() => {
    if (!open || !consulta) return;
    setError('');
    setCreado(null);
    setNotas('');

    fetch(`${API_URL}/emr/finanzas/servicios?activo=true`, {
      headers: { 'X-User-Id': userId },
    })
      .then(r => r.ok ? r.json() : [])
      .then((srvs) => {
        setServicios(srvs);
        const sugerido = buscarServicioPorTipo(srvs, consulta.tipo_consulta);
        if (sugerido) {
          setItems([{
            _id: `item_${Date.now()}`,
            servicio_id: sugerido.id,
            descripcion: sugerido.nombre,
            cantidad: 1,
            precio_unitario: Number(sugerido.precio),
            descuento: 0,
          }]);
          setMoneda(sugerido.moneda || 'MXN');
        } else {
          // Sin sugerencia: 1 item vacío
          setItems([{
            _id: `item_${Date.now()}`,
            servicio_id: null,
            descripcion: '',
            cantidad: 1,
            precio_unitario: 0,
            descuento: 0,
          }]);
        }
      })
      .catch(console.error);
  }, [open, consulta, userId]);

  const updateItem = (id, cambios) => {
    setItems(items.map(it => it._id === id ? { ...it, ...cambios } : it));
  };

  const removeItem = (id) => {
    setItems(items.filter(it => it._id !== id));
  };

  const addItem = () => {
    setItems([...items, {
      _id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      servicio_id: null,
      descripcion: '',
      cantidad: 1,
      precio_unitario: 0,
      descuento: 0,
    }]);
  };

  const handleSelectServicio = (id, servicio) => {
    if (!servicio) return;
    updateItem(id, {
      servicio_id: servicio.id,
      descripcion: servicio.nombre,
      precio_unitario: Number(servicio.precio),
    });
  };

  const totales = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (Number(it.cantidad) * Number(it.precio_unitario)), 0);
    const descuento = items.reduce((s, it) => s + Number(it.descuento || 0), 0);
    return { subtotal, descuento, total: Math.max(0, subtotal - descuento) };
  }, [items]);

  const valido = items.length > 0
    && items.every(it => it.descripcion.trim().length >= 2 && Number(it.cantidad) > 0 && Number(it.precio_unitario) >= 0);

  const handleCrearCargos = async () => {
    if (!valido) return;
    setGuardando(true);
    setError('');
    try {
      const body = {
        paciente_id: paciente.id,
        consulta_id: consulta.id,
        moneda,
        notas: notas.trim() || null,
        items: items.map(it => ({
          servicio_id: it.servicio_id,
          descripcion: it.descripcion.trim(),
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
          descuento: Number(it.descuento || 0),
        })),
      };
      const res = await fetch(`${API_URL}/emr/finanzas/cargos/masivo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.substring(0, 250));
      }
      const cargos = await res.json();
      setCreado(cargos);
      onCargoCreado?.(cargos);
    } catch (e) {
      setError(e.message || 'Error al crear cargos');
    } finally {
      setGuardando(false);
    }
  };

  if (!consulta) return null;

  return (
    <Dialog
      open={open}
      onClose={() => !guardando && onClose()}
      TransitionComponent={SlideUp}
      maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(15,23,42,0.25)', overflow: 'hidden',
        },
      }}
    >
      {/* Header slim */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)', color: '#fff',
      }}>
        <MoneyIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 }}>
            Cobrar consulta
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.8 }}>
            {paciente?.nombre_completo} · Consulta #{consulta.id}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={guardando}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4 }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* PASO 2: cargo creado, ofrecer registrar pago */}
      {creado ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 1.5,
            boxShadow: '0 8px 20px rgba(21,128,61,0.3)',
          }}>
            <CheckIcon sx={{ fontSize: 32, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
            Cargo registrado
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b', mb: 0.5 }}>
            Total: <b>{fmtMoney(totales.total, moneda)}</b>
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mb: 2.5 }}>
            ¿Cómo quieres proceder con el pago?
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexDirection: 'column', maxWidth: 320, mx: 'auto' }}>
            <Button
              fullWidth variant="contained"
              onClick={() => {
                onClose();
                // Abrir DialogNuevoPago con el primer cargo creado
                if (creado[0]) onAbrirPago?.(creado[0]);
              }}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 13, borderRadius: 2, py: 1.1,
                background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
                boxShadow: '0 4px 12px rgba(21,128,61,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #14532d 0%, #15803d 100%)' },
              }}
            >
              Registrar pago ahora
            </Button>
            <Button
              fullWidth onClick={onClose}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: 2, py: 1,
                color: '#475569', background: '#f1f5f9',
                '&:hover': { background: '#e2e8f0' },
              }}
            >
              Dejar pendiente
            </Button>
          </Box>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', mt: 1.2 }}>
            Si lo dejas pendiente, podrás cobrarlo después desde el tab "Cuenta" del paciente
          </Typography>
        </Box>
      ) : (
        <>
          {/* Body */}
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '64vh', overflowY: 'auto' }}>

            {/* Items */}
            {items.map((item, idx) => (
              <ItemRow
                key={item._id}
                item={item}
                servicios={servicios}
                indice={idx}
                puedeEliminar={items.length > 1}
                onUpdate={(c) => updateItem(item._id, c)}
                onRemove={() => removeItem(item._id)}
                onSelectServicio={(s) => handleSelectServicio(item._id, s)}
              />
            ))}

            {/* Add item */}
            <Button
              size="small" startIcon={<AddIcon />}
              onClick={addItem}
              sx={{
                textTransform: 'none', fontSize: 12, fontWeight: 600,
                color: '#1e3a5f', borderRadius: 2,
                alignSelf: 'flex-start',
                '&:hover': { background: '#eff6ff' },
              }}
            >
              Agregar otro item
            </Button>

            {/* Notas */}
            <TextField
              sx={inputStyle} size="small"
              label="Notas del cargo (opcional)"
              placeholder="Detalles del cobro..."
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />

            {/* Total */}
            <Box sx={{
              p: 1.5, borderRadius: 2,
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '1px solid #bfdbfe',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Box>
                <Typography sx={{ fontSize: 10, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total a cobrar
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.1 }}>
                  {items.length} item{items.length !== 1 ? 's' : ''} · subtotal {fmtMoney(totales.subtotal, moneda)}
                  {totales.descuento > 0 && ` − descuento ${fmtMoney(totales.descuento, moneda)}`}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#1e3a5f', lineHeight: 1 }}>
                {fmtMoney(totales.total, moneda)}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ py: 0.4, fontSize: 12 }}>
                {error}
              </Alert>
            )}
          </Box>

          {/* Acciones */}
          <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
            <Button
              fullWidth onClick={onClose} disabled={guardando}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: 2, py: 1,
                color: '#475569', background: '#f1f5f9',
                '&:hover': { background: '#e2e8f0' },
              }}
            >
              Saltar
            </Button>
            <Button
              fullWidth variant="contained"
              onClick={handleCrearCargos} disabled={!valido || guardando}
              startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 2, py: 1,
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
                '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
              }}
            >
              {guardando ? 'Creando...' : `Crear cargo ${fmtMoney(totales.total, moneda)}`}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
}

// ============================================================================
function ItemRow({ item, servicios, indice, puedeEliminar, onUpdate, onRemove, onSelectServicio }) {
  const subtotal = (Number(item.cantidad) || 0) * (Number(item.precio_unitario) || 0);
  const total = Math.max(0, subtotal - (Number(item.descuento) || 0));

  return (
    <Box sx={{
      p: 1.2, borderRadius: 2,
      border: '1px solid #e2e8f0', background: '#fff',
      display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Chip label={`#${indice + 1}`} size="small" sx={{
          height: 18, fontSize: 10, fontWeight: 700,
          background: '#f1f5f9', color: '#64748b',
        }} />
        <Box sx={{ flex: 1 }} />
        {puedeEliminar && (
          <Tooltip title="Quitar item">
            <IconButton size="small" onClick={onRemove}
              sx={{ color: '#dc2626', p: 0.4, '&:hover': { background: '#fef2f2' } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Selector de servicio */}
      <Autocomplete
        size="small" sx={inputStyle}
        options={servicios}
        getOptionLabel={(o) => o.nombre || ''}
        value={servicios.find(s => s.id === item.servicio_id) || null}
        onChange={(_, v) => onSelectServicio(v)}
        renderInput={(p) => (
          <TextField {...p} label="Servicio del catálogo" placeholder="Buscar..." />
        )}
        renderOption={(props, opt) => (
          <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between !important', gap: 1 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{opt.nombre}</Typography>
            <Chip
              label={`$${Number(opt.precio).toLocaleString('es-MX')}`} size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1e3a5f' }}
            />
          </Box>
        )}
      />

      {/* Descripción (editable) */}
      <TextField
        sx={inputStyle} size="small"
        label="Descripción"
        value={item.descripcion}
        onChange={(e) => onUpdate({ descripcion: e.target.value })}
      />

      {/* Cantidad / precio / descuento */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          sx={{ ...inputStyle, flex: 0.6 }} size="small"
          type="number" label="Cantidad"
          value={item.cantidad}
          onChange={(e) => onUpdate({ cantidad: e.target.value })}
          inputProps={{ min: 0, step: '0.01' }}
        />
        <TextField
          sx={{ ...inputStyle, flex: 1 }} size="small"
          type="number" label="Precio"
          value={item.precio_unitario}
          onChange={(e) => onUpdate({ precio_unitario: e.target.value })}
          inputProps={{ min: 0, step: '0.01' }}
        />
        <TextField
          sx={{ ...inputStyle, flex: 0.7 }} size="small"
          type="number" label="Descuento"
          value={item.descuento}
          onChange={(e) => onUpdate({ descuento: e.target.value })}
          inputProps={{ min: 0, step: '0.01' }}
        />
      </Box>

      {/* Subtotal del item */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.8 }}>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          Subtotal del item:
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e3a5f' }}>
          {fmtMoney(total, 'MXN')}
        </Typography>
      </Box>
    </Box>
  );
}
